import { Injectable } from 'angular-ts-decorators';

import { LegacyBase } from '@shared/app/scripts/services/legacy-base';
import { ILegacyStockObject } from '@classes/legacy-stock-object';
import { ILegacyFullStockObject } from '@classes/legacy-full-stock-object';
import type { Uuid } from '@shared/app/scripts/vendor/uuid';

export interface ILegacyProductsInSetModel {
  code: string;
  description: string;
  ean: string;
}

/**
 * This service is used to interact with the FoxPro API. It's meant to be integrated
 * with v6's legacy service & moved to shared module - same as the one in the products
 * module. The traffic is sent via Spynl services
 */
@Injectable('legacy')
export class Legacy {
  constructor(
    private legacyBase: LegacyBase,
    private uuid: Uuid,
  ) {
    'ngInject';
  }

  /**
   * @param {string} warehouseId id of the warehouse in FoxPro
   * Get's the EOD information from the FoxPro API.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getEOD(warehouseId: string): ng.IPromise<any> {
    return this.legacyBase.callApi('getEOD', {
      warehouse: escape(warehouseId),
    });
  }

  /**
   * @param {string} couponId id of the Coupon in FoxPro. There is
   * a timeout has been added to the request because the FoxPro
   * API can be so slow that the request might hang. This is legacy
   * that is inherited from the sw6 codebase.
   * Gets information about a coupon from the FoxPro API
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getCoupon(couponId: string): ng.IPromise<any> {
    return this.legacyBase.callApi(
      'raptorcoupon',
      {
        coupon: escape(couponId),
      },
      { timeout: 10000 },
    );
  }

  /**
   * @param {string} couponId id of the Coupon in FoxPro.
   * Blocks a coupon in FoxPro. Blocked coupons are returned with
   * status "BLOCKED" and cannot be redeemed.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  blockCoupon(couponId: string): ng.IPromise<any> {
    return this.legacyBase.callApi('blockcoupon', { coupon: escape(couponId) });
  }

  /**
   * @param {string} couponId id of the Coupon in FoxPro.
   * Unblocks a blocked coupon so it can be redeemed again.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  unblockCoupon(couponId: string): ng.IPromise<any> {
    return this.legacyBase.callApi('blockcoupon', {
      coupon: escape(couponId),
      deblock: true,
    });
  }

  /**
   * @param {string} uuid id of the customer
   * a timeout has been added to the request because the FoxPro
   * API can be so slow that the request might hang. This is legacy
   * that is inherited from the sw6 codebase.
   * Returns if the customer has any open coupons
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getOpenCouponsByCustomer(uuid: string): ng.IPromise<any> {
    return this.legacyBase.callApi(
      'getopencoupons',
      {
        uuid: escape(uuid),
      },
      { timeout: 10000 },
    );
  }

  /**
   * @param {string} uuid customer _id
   * Gets the coupon history of a consumer. There is
   * a timeout has been added to the request because the FoxPro
   * API can be so slow that the request might hang. This is legacy
   * that is inherited from the sw6 codebase.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getStoreCredit(uuid: string): ng.IPromise<any> {
    return this.legacyBase.callApi(
      'getstorecredit',
      {
        uuid,
      },
      { timeout: 10000 },
    );
  }

  /**
   * @param {string} uuid customer uuid
   * Gets the coupon history of a consumer
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getCustomerCoupons(uuid: string): ng.IPromise<any> {
    return this.legacyBase.callApi('raptorclientdocs', {
      uuid,
      isodate: 'true',
    });
  }

  /**
   * @param {string} uuid customer _id
   * Gets information from FoxPro API regarding the order history of wholesale customers
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getWholesaleCustomerHistory(uuid: string): ng.IPromise<any> {
    return this.legacyBase
      .callApi('wgetstatus', {
        uuid,
      })
      .then((response) => response.lines);
  }

  /**
   * @param {string} uuid customer uuid
   * Get consumer/wholesale customer data from the FoxPro API.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getClient(uuid: string): ng.IPromise<any> {
    return this.legacyBase.callApi('getClient', {
      uuid,
      switches: 'groups',
    });
  }

  /**
   * @param {string} barcode article code
   * get data per SKU from the FoxPro API by barcode. There is
   * a timeout has been added to the request because the FoxPro
   * API can be so slow that the request might hang. This is legacy
   * that is inherited from the sw6 codebase.
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getSku(barcode: string): ng.IPromise<any> {
    return this.legacyBase.callApi('raptorsku', {
      barcode: escape(barcode),
    });
  }

  /**
   * @param {string} barcode article code
   * get SKU by using a concatenation  of articleCode + colorCode.
   * Only used in Polytex code in sw6
   * @returns {ng.IPromise<any>} resolve with fetched data
   */
  getSkuByArticleCodeAndColorCode(barcode: string): ng.IPromise<any> {
    return this.legacyBase.callApi('raptorsku', {
      barcode: escape(barcode),
      extendedsearch: 'true',
    });
  }

  /**
   * @param {string} articleCode article code
   * Get article data for the sales from the FoxPro API.
   * Economical Stock is the amount of stock to be produced.
   * @returns {ng.IPromise<ILegacyStockObject[]>} resolve with fetched data
   */
  // TODO double check if 'barcode' is meant to be expected here
  getEconomicalStock(articleCode: string): ng.IPromise<ILegacyStockObject[]> {
    return this.legacyBase
      .callApi('getEStock', {
        articlecode: escape(articleCode),
      })
      .then((response) => response.stock);
  }

  /**
   * @param {string} barcode article barcode
   * Get the amount of products in stock from the FoxPro API by its barcode
   * @returns {ng.IPromise<ILegacyFullStockObject[]>} resolve with fetched data
   */
  getStockByBarcode(barcode: string): ng.IPromise<ILegacyFullStockObject[]> {
    return this.legacyBase
      .callApi('getStock4', {
        barcode: escape(barcode),
        switches: 'perShop',
        set: 'true',
      })
      .then((response) => {
        const matchingElement = response.stock.find(
          (row) => row.barcode === escape(barcode),
        );

        if (!matchingElement) {
          return response.stock;
        }

        return response.stock.filter(
          (row) => row.code === matchingElement.code,
        );
      });
  }

  /**
   * @param {string} articleCode article code
   * Get the amount of products in stock from the FoxPro API by its article code
   * @returns {httpPromise} resolve with fetched data
   */
  getStockByArticleCode(
    articleCode: string,
  ): ng.IPromise<ILegacyFullStockObject[]> {
    return this.legacyBase
      .callApi('getStock', {
        articlecode: escape(articleCode),
      })
      .then((response) => response.stock);
  }

  /**
   * @param {string} articleCode article code
   * @param {string} storage quantity in format <storageLong>,<storageFolded>
   * Set storage value for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  setStorage(articleCode: string, storage: string): ng.IPromise<any> {
    return this.legacyBase.callApi('setstorage', {
      articlecode: escape(articleCode),
      // no escape(), as it should be just number & coma
      storage,
    });
  }

  /**
   * @param {string} barcode barcode
   * @param {number} remaining number of remaining, will be multiplied by -1 before saving
   * @param {number} foldingLength folding length
   * @param {string} operator folding operator. 3 char abbr
   * add to inv for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  add2inv(
    barcode: string,
    remaining: number,
    foldingLength: number,
    operator: string,
  ): ng.IPromise<any> {
    return this.legacyBase.callApi('raptoradd2inv', {
      barcode,
      qty: remaining * -1,
      originalLength: foldingLength,
      operator,
    });
  }

  /**
   * @param {string} szindex szindex
   * @param {string} prev prev
   * @param {string} deliver deliver
   * @param {string} charge charge
   * @param {string} operator folding operator. 3 char abbr
   * add 2 pb for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  add2pb(
    szindex: string,
    prev: string,
    deliver: string,
    charge: string,
    operator: string,
  ): ng.IPromise<any> {
    return this.legacyBase.callApi('raptoradd2pb', {
      szindex,
      prev,
      deliver,
      charge,
      operator,
      uuid: this.uuid.v4(),
    });
  }

  /**
   * @param {string} barcode barcode
   * @param {string} prev prev
   * @param {string} deliver deliver
   * @param {string} charge charge
   * @param {string} operator folding operator. 3 char abbr
   * add to tr for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  add2tr(
    barcode: string,
    prev: string,
    deliver: string,
    charge: string,
    operator: string,
  ): ng.IPromise<any> {
    return this.legacyBase.callApi('raptoradd2tr', {
      barcode,
      prev,
      deliver,
      charge,
      operator,
      uuid: this.uuid.v4(),
    });
  }

  /**
   * @param {string} barcode barcode
   * folding for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  folding(barcode: string): ng.IPromise<any> {
    return this.legacyBase.callApi('raptorfold', {
      barcode,
    });
  }

  /**
   * @param {string} barcode barcode
   * Get stock for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  getStock3(barcode: string): ng.IPromise<[][]> {
    return this.legacyBase
      .callApi('getstock3', {
        barcode,
      })
      .then((response) => response.stock);
  }

  /**
   * @param {string} barcode barcode
   * Get stock for polytex
   * @returns {httpPromise} resolve with fetched data
   */
  getStock5(barcode: string): ng.IPromise<{
    productsInSet: ILegacyProductsInSetModel[];
    stock: ILegacyFullStockObject[];
  }> {
    return this.legacyBase.callApi('getStock5', {
      barcode: escape(barcode),
      switches: 'perShop',
      set: 'true',
    });
  }
}
